import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['amount', 'error', 'submit', 'walletStatus', 'connect', 'withdraw']
  static values = {
    nftNumber: String,
    marketplaceContract: String,
    chainListingId: String,
    verifiedAddress: String,
    walletVerified: Boolean,
    minimumBidWei: String
  }

  connect () {
    this.restoreWallet()
  }

  async connectWallet () {
    try {
      if (!globalThis.ethereum) throw new Error('Install MetaMask to bid.')
      await this.ensureSepolia()
      const [address] = await globalThis.ethereum.request({ method: 'eth_requestAccounts' })
      if (!address) throw new Error('No MetaMask account was selected.')
      const challenge = await this.requestJson('/nfts/wallet_challenge', { method: 'POST', body: { address } })
      const signature = await globalThis.ethereum.request({ method: 'personal_sign', params: [challenge.message, address] })
      await this.requestJson('/nfts/wallet_connect', {
        method: 'POST', body: { address, nonce: challenge.nonce, signature }
      })
      this.verifiedAddressValue = address.toLowerCase()
      this.walletVerifiedValue = true
      await this.showConnected(address)
    } catch (error) {
      this.showError(error.message)
    }
  }

  async placeBid (event) {
    event.preventDefault()
    try {
      const bidder = await this.activeVerifiedWallet()
      const amountWei = this.parseEther(this.amountTarget.value)
      if (amountWei < BigInt(this.minimumBidWeiValue)) {
        throw new Error(`Minimum bid is ${this.formatEther(this.minimumBidWeiValue)} ETH.`)
      }
      this.setBusy(true, 'Waiting for MetaMask...')
      const txHash = await globalThis.ethereum.request({
        method: 'eth_sendTransaction',
        params: [{
          from: bidder,
          to: this.marketplaceContractValue,
          value: `0x${amountWei.toString(16)}`,
          data: `0x454a2ab3${this.uintWord(this.chainListingIdValue)}`
        }]
      })
      this.setBusy(true, 'Confirming on Sepolia...')
      await this.waitForReceipt(txHash)
      const result = await this.requestJson(`/nfts/${this.nftNumberValue}/place_bid`, {
        method: 'POST', body: { tx_hash: txHash }
      })
      this.minimumBidWeiValue = result.minimum_bid_wei
      this.amountTarget.value = this.formatEther(result.minimum_bid_wei)
      this.setBusy(false)
      globalThis.location.reload()
    } catch (error) {
      this.setBusy(false)
      this.showError(error.message || 'Bid failed.')
    }
  }

  async withdrawRefund () {
    try {
      const account = await this.activeVerifiedWallet()
      this.setBusy(true, 'Withdrawing escrow refund...')
      const txHash = await globalThis.ethereum.request({
        method: 'eth_sendTransaction',
        params: [{ from: account, to: this.marketplaceContractValue, data: '0x3ccfd60b' }]
      })
      await this.waitForReceipt(txHash)
      this.setBusy(false)
      this.withdrawTarget.classList.add('d-none')
    } catch (error) {
      this.setBusy(false)
      this.showError(error.message || 'Refund withdrawal failed.')
    }
  }

  async restoreWallet () {
    if (!this.walletVerifiedValue || !globalThis.ethereum || !this.verifiedAddressValue) return
    const [account] = await globalThis.ethereum.request({ method: 'eth_accounts' })
    if (account?.toLowerCase() === this.verifiedAddressValue.toLowerCase()) await this.showConnected(account)
  }

  async showConnected (address) {
    this.walletStatusTarget.textContent = `${this.short(address)} · Sepolia verified`
    this.connectTarget.classList.add('d-none')
    this.submitTarget.disabled = false
    const pending = await globalThis.ethereum.request({
      method: 'eth_call',
      params: [{
        to: this.marketplaceContractValue,
        data: `0xf3f43703${this.addressWord(address)}`
      }, 'latest']
    })
    this.withdrawTarget.classList.toggle('d-none', BigInt(pending) === 0n)
    if (BigInt(pending) > 0n) this.withdrawTarget.textContent = `Withdraw ${this.formatEther(BigInt(pending))} ETH refund`
  }

  async activeVerifiedWallet () {
    if (!this.walletVerifiedValue) throw new Error('Connect and verify your Sepolia wallet first.')
    await this.ensureSepolia()
    const [account] = await globalThis.ethereum.request({ method: 'eth_requestAccounts' })
    if (account?.toLowerCase() !== this.verifiedAddressValue.toLowerCase()) {
      throw new Error('The active MetaMask account does not match your verified wallet.')
    }
    return account.toLowerCase()
  }

  async ensureSepolia () {
    const chainId = await globalThis.ethereum.request({ method: 'eth_chainId' })
    if (chainId.toLowerCase() !== '0xaa36a7') {
      await globalThis.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: '0xaa36a7' }] })
    }
  }

  async waitForReceipt (txHash) {
    const deadline = Date.now() + 180000
    while (Date.now() < deadline) {
      const receipt = await globalThis.ethereum.request({ method: 'eth_getTransactionReceipt', params: [txHash] })
      if (receipt) {
        if (BigInt(receipt.status || '0x0') !== 1n) throw new Error('The auction transaction reverted.')
        return receipt
      }
      await new Promise((resolve) => setTimeout(resolve, 2500))
    }
    throw new Error('Transaction is still pending. Check Sepolia Etherscan.')
  }

  async requestJson (path, options) {
    const response = await fetch(path, {
      method: options.method,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector("[name='csrf-token']").content },
      body: JSON.stringify(options.body || {})
    })
    const text = await response.text()
    const payload = text ? JSON.parse(text) : {}
    if (!response.ok) throw new Error(payload.errors?.join(', ') || payload.error || 'Request failed.')
    return payload
  }

  parseEther (value) {
    if (!/^\d+(\.\d{1,18})?$/.test(value.toString())) throw new Error('Enter a valid ETH amount.')
    const [whole, fraction = ''] = value.toString().split('.')
    return BigInt(whole) * 10n ** 18n + BigInt(fraction.padEnd(18, '0'))
  }

  formatEther (value) {
    const wei = BigInt(value)
    const whole = wei / 10n ** 18n
    const fraction = (wei % 10n ** 18n).toString().padStart(18, '0').replace(/0+$/, '')
    return fraction ? `${whole}.${fraction}` : whole.toString()
  }

  uintWord (value) { return BigInt(value).toString(16).padStart(64, '0') }
  addressWord (value) { return value.toLowerCase().replace(/^0x/, '').padStart(64, '0') }
  short (value) { return `${value.slice(0, 8)}...${value.slice(-6)}` }
  showError (message) { this.errorTarget.textContent = message; this.errorTarget.classList.remove('d-none') }
  setBusy (busy, label = 'Place bid') { this.submitTarget.disabled = busy; this.submitTarget.textContent = label }
}
